import { HttpService } from '@/data/http/http-service';
import { calculateVenueEnvDtoSchema } from '@/data/http/impact-calculate-venue/env/dto.type.ts';
import { CalculateVenueEnvMapper } from '@/data/http/impact-calculate-venue/env/mapper.ts';
import { calculateVenueEssDtoSchema } from '@/data/http/impact-calculate-venue/ess/dto.type.ts';
import { CalculateVenueEssMapper } from '@/data/http/impact-calculate-venue/ess/mapper.ts';
import { CalculateVenue } from '@/domain/impact-calculate-venue/entities/calculate-venue.entity';
import { CalculateVenueCo2 } from '@/domain/impact-calculate-venue/entities/calculate-venue-co2.entity';
import { CalculateVenueEnv } from '@/domain/impact-calculate-venue/entities/calculate-venue-env.entity';
import { CalculateVenueEss } from '@/domain/impact-calculate-venue/entities/calculate-venue-ess.entity';
import { ImpactCalculateVenueGateway } from '@/domain/impact-calculate-venue/gateways/impact-calculate-venue.gateway';
import {
  ModelVersionENV,
  ModelVersionESS,
} from '@/domain/project/entities/project.entity';

import { calculateVenueCo2DtoSchema } from './co2/dto.type';
import { CalculateVenueCo2Mapper } from './co2/mapper';
import { resultsVenueDtoSchema } from './results/dto.type';
import { ResultsVenueMapper } from './results/mapper';
import { resultsVenueMonthDtoSchema } from './results-month/dto.type';
import { ResultsVenueMonthMapper } from './results-month/mapper';
import { getSummaryImpactCalculateVenueForAllProjectsDtoSchema } from './summary-impact-for-all-projects/dto.schema';
import { SummaryImpactForAllProjectsMapper } from './summary-impact-for-all-projects/mapper';

export class ApiImpactCalculateVenueHttpGateway implements ImpactCalculateVenueGateway {
  private httpProject = new HttpService({
    baseURL: import.meta.env.VITE_API_PROJECT,
  });
  private httpImpact = new HttpService({
    baseURL: import.meta.env.VITE_API_IMPACT,
  });

  // project-venue CO2

  async runImpactCalculateVenueCo2ForProject(
    projectId: number,
    modelVersion: ModelVersionENV,
  ): Promise<CalculateVenueCo2> {
    const url = '/calculate/co2/venue';
    const data = { seasonId: projectId };
    const options = { additionalHeaders: { 'x-model-version': modelVersion } };
    const response = await this.httpImpact.post(url, data, options);
    const result = calculateVenueCo2DtoSchema.safeParse(response.data);
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    const calculateVenueCo2 = CalculateVenueCo2Mapper.dtoToEntity(result.data);
    return calculateVenueCo2;
  }

  // project-venue ENV

  async runImpactCalculateVenueEnvForProject(
    projectId: number,
    modelVersion: ModelVersionENV,
  ): Promise<CalculateVenueEnv> {
    const url = '/calculate/env/venue-season';
    const options = { additionalHeaders: { 'x-model-version': modelVersion } };
    const data = { seasonId: projectId };
    const response = await this.httpImpact.post(url, data, options);
    const result = calculateVenueEnvDtoSchema.safeParse(response.data);
    if (!result.success) {
      console.error(result.error);
      throw result.error;
    }
    return CalculateVenueEnvMapper.dtoToEntity(result.data);
  }

  // project-venue ESS

  async runImpactCalculateVenueEssForProject(
    projectId: number,
    modelVersion: ModelVersionESS,
  ): Promise<CalculateVenueEss> {
    const url = '/calculate/ess/venue-season';
    const data = { seasonId: projectId };
    const options = { additionalHeaders: { 'x-model-version': modelVersion } };
    const response = await this.httpImpact.post(url, data, options);
    const result = calculateVenueEssDtoSchema.safeParse(response.data);
    if (!result.success) {
      console.error(result.error);
      throw result.error;
    }
    return CalculateVenueEssMapper.dtoToEntity(result.data);
  }

  // project-venue date CO2

  async runImpactCalculateVenueCo2ForDate(
    dateId: number,
    projectId: number,
    modelEnvVersion: ModelVersionENV,
  ): Promise<void> {
    const url = `/calculate/co2/venue-date`;
    const data = { seasonId: projectId, dateId };
    const options = {
      additionalHeaders: { 'x-model-version': modelEnvVersion },
    };
    const response = await this.httpImpact.post(url, data, options);
    // we could use the return
    if (response.status === 201) {
      return;
    } else {
      throw 'error run impact calculate co2 for date';
    }
  }

  // project-venue date ESS

  async runImpactCalculateVenueEssForDate(
    dateId: number,
    projectId: number,
    modelVersion: ModelVersionESS,
  ): Promise<void> {
    const url = '/calculate/ess/date';
    const data = { seasonId: projectId, dateId };
    const options = { additionalHeaders: { 'x-model-version': modelVersion } };
    const response = await this.httpImpact.post(url, data, options);
    // we could use the return
    if (response.status === 201) {
      return;
    } else {
      throw 'error run impact calculate ESS for date';
    }
  }

  // results

  async getVenueResultsForProject(projectId: number): Promise<CalculateVenue> {
    const callVenue = async () => {
      const url = `/results/venue-season/${projectId}`;
      const response = await this.httpProject.get(url);
      const result = resultsVenueDtoSchema.safeParse(response.data);
      if (result.success === false) {
        console.error(result.error);
        throw result.error;
      }
      return ResultsVenueMapper.dtoToEntity(result.data);
    };
    const callVenueMonth = async () => {
      const url = `/results/venue-season/${projectId}/month`;
      const response = await this.httpProject.get(url);
      const result = resultsVenueMonthDtoSchema.safeParse(response.data);
      if (result.success === false) {
        console.error(result.error);
        throw result.error;
      }
      const dates = ResultsVenueMonthMapper.dtoToEntity(result.data);
      return { dates };
    };
    const calculateVenue = await callVenue();
    const { dates } = await callVenueMonth();
    dates.score = calculateVenue.co2?.dates?.score || 0;

    if (!calculateVenue.co2) {
      throw Error('Invalid co2 calculate');
    }
    return {
      ...calculateVenue,
      co2: {
        global: calculateVenue.co2.global,
        structure: calculateVenue.co2.structure,
        dates: dates,
      },
    };
  }

  async getSummaryImpactCalculateVenueForAllProjects(): Promise<
    { projectId: number; calculate: CalculateVenue }[]
  > {
    const url = '/seasons';
    const options = { params: { $limit: 1000 } };
    const response = await this.httpProject.get(url, options);
    const result =
      getSummaryImpactCalculateVenueForAllProjectsDtoSchema.safeParse(
        response.data,
      );
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    const calculate = SummaryImpactForAllProjectsMapper.dtoToEntity(
      result.data,
    );
    return calculate;
  }
}
